import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Logging } from '../settings/Logging';
import { Logs } from '../settings/Logs';
import { ExplorerTab } from '../settings/ExplorerTab';
import { DriveBackups } from './DriveBackups';
import { Diagnosis } from '../settings/Diagnosis';
import { LlmMetrics } from '../settings/LlmMetrics';
import { PagePerf } from '../settings/PagePerf';
import { Messages } from './Messages';
import { AsyncJobs } from './AsyncJobs';
import { ContainerStats } from './ContainerStats';
import { ErrorStats } from './ErrorStats';
import { SessionIssues } from './SessionIssues';
import { QuestionQuality } from './questionQuality/QuestionQuality';
import '../settings/Settings.css';

type MonitoringTab = 'logging' | 'logs' | 'messages' | 'async-jobs' | 'container-stats' | 'error-stats' | 'session-issues' | 'drive-backups' | 'llm-metrics' | 'page-perf' | 'explorer' | 'diagnosis' | 'question-quality';

export function Monitoring() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: MonitoringTab = (searchParams.get('tab') as MonitoringTab) || 'logging';

  const explorerInitialContainer = searchParams.get('container') || undefined;
  const explorerInitialId = searchParams.get('id') || undefined;

  const setActiveTab = useCallback((tab: MonitoringTab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      if (tab !== 'explorer') {
        next.delete('container');
        next.delete('id');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return (
    <div className="settings-page" data-active-tab={activeTab}>
      <h1>Monitoring</h1>

      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'logging' ? 'active' : ''}`}
          onClick={() => setActiveTab('logging')}
        >
          Logging
        </button>
        <button
          className={`settings-tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          Logs
        </button>
        <button
          className={`settings-tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          Messages
        </button>
        <button
          className={`settings-tab ${activeTab === 'async-jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('async-jobs')}
        >
          Async Jobs
        </button>
        <button
          className={`settings-tab ${activeTab === 'container-stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('container-stats')}
        >
          Container Stats
        </button>
        <button
          className={`settings-tab ${activeTab === 'error-stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('error-stats')}
        >
          Error Stats
        </button>
        <button
          className={`settings-tab ${activeTab === 'session-issues' ? 'active' : ''}`}
          onClick={() => setActiveTab('session-issues')}
        >
          Session Issues
        </button>
        <button
          className={`settings-tab ${activeTab === 'drive-backups' ? 'active' : ''}`}
          onClick={() => setActiveTab('drive-backups')}
        >
          Drive Backups
        </button>
        <button
          className={`settings-tab ${activeTab === 'llm-metrics' ? 'active' : ''}`}
          onClick={() => setActiveTab('llm-metrics')}
        >
          LLM Metrics
        </button>
        <button
          className={`settings-tab ${activeTab === 'page-perf' ? 'active' : ''}`}
          onClick={() => setActiveTab('page-perf')}
        >
          Page Perf
        </button>
        <button
          className={`settings-tab ${activeTab === 'explorer' ? 'active' : ''}`}
          onClick={() => setActiveTab('explorer')}
        >
          Explorer
        </button>
        <button
          className={`settings-tab ${activeTab === 'diagnosis' ? 'active' : ''}`}
          onClick={() => setActiveTab('diagnosis')}
        >
          Diagnosis
        </button>
        <button
          className={`settings-tab ${activeTab === 'question-quality' ? 'active' : ''}`}
          onClick={() => setActiveTab('question-quality')}
        >
          Question Quality
        </button>
      </div>

      <div style={{ display: activeTab === 'logging' ? undefined : 'none' }}><Logging /></div>
      <div style={{ display: activeTab === 'logs' ? undefined : 'none' }}><Logs /></div>
      <div style={{ display: activeTab === 'messages' ? undefined : 'none' }}><Messages /></div>
      <div style={{ display: activeTab === 'async-jobs' ? undefined : 'none' }}><AsyncJobs /></div>
      <div style={{ display: activeTab === 'container-stats' ? undefined : 'none' }}><ContainerStats /></div>
      <div style={{ display: activeTab === 'error-stats' ? undefined : 'none' }}><ErrorStats /></div>
      <div style={{ display: activeTab === 'session-issues' ? undefined : 'none' }}><SessionIssues /></div>
      <div style={{ display: activeTab === 'drive-backups' ? undefined : 'none' }}><DriveBackups /></div>
      <div style={{ display: activeTab === 'llm-metrics' ? undefined : 'none' }}><LlmMetrics /></div>
      <div style={{ display: activeTab === 'page-perf' ? undefined : 'none' }}><PagePerf /></div>
      <div style={{ display: activeTab === 'explorer' ? undefined : 'none' }}>
        <div className="settings-section">
          <div className="section-header">
            <h2>Object Explorer</h2>
            <p className="section-description">
              Browse CosmosDB documents and navigate FK relationships. Click highlighted fields to jump to related documents.
            </p>
          </div>
          <ExplorerTab
            initialContainer={explorerInitialContainer}
            initialDocId={explorerInitialId}
          />
        </div>
      </div>
      <div style={{ display: activeTab === 'diagnosis' ? undefined : 'none' }}><Diagnosis /></div>
      <div style={{ display: activeTab === 'question-quality' ? undefined : 'none' }}><QuestionQuality /></div>
    </div>
  );
}
